/**
 * TenantCurrencySelector - Selection de la devise principale du tenant
 * 
 * Affiche les devises systeme disponibles et permet au tenant
 * de choisir sa devise principale.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { RefreshCw, Check, Globe, DollarSign } from 'lucide-react';
import { safeFixed } from '../utils/formatters';

export default function TenantCurrencySelector() {
  const [currencies, setCurrencies] = useState([]);
  const [primaryCurrency, setPrimaryCurrency] = useState('MAD');
  const [baseCurrency, setBaseCurrency] = useState('MAD');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/currencies/tenant');
      setCurrencies(res.data.currencies || []);
      setPrimaryCurrency(res.data.primary_currency || 'MAD');
      setBaseCurrency(res.data.base_currency || 'MAD');
    } catch (error) {
      console.error('Error fetching tenant currency:', error);
      toast.error('Erreur lors du chargement des devises');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSelectCurrency = async (code) => {
    if (code === primaryCurrency) return;
    
    try {
      setSaving(true);
      await api.put('/currencies/tenant', { currency_code: code });
      setPrimaryCurrency(code);
      toast.success(`Devise principale definie: ${code}`);
    } catch (error) {
      toast.error('Erreur lors de la mise a jour');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card data-testid="tenant-currency-selector">
        <CardContent className="py-8">
          <div className="flex justify-center">
            <RefreshCw className="w-6 h-6 animate-spin text-primary" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="tenant-currency-selector">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
            <Globe className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <CardTitle className="text-lg">Devise principale</CardTitle>
            <CardDescription>
              Selectionnez la devise utilisee par defaut pour votre riad
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {currencies.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <DollarSign className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p>Aucune devise systeme disponible</p>
            <p className="text-sm">Contactez l'administrateur</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {currencies.map(currency => {
              const isSelected = currency.code === primaryCurrency;
              const isBase = currency.code === baseCurrency;
              
              return (
                <button
                  key={currency.code}
                  onClick={() => handleSelectCurrency(currency.code)}
                  disabled={saving || isSelected}
                  className={`relative flex items-center gap-3 p-4 rounded-lg border-2 text-left transition-all ${
                    isSelected 
                      ? 'border-primary bg-primary/5' 
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  } ${saving ? 'opacity-50' : ''}`}
                  data-testid={`currency-option-${currency.code}`}
                >
                  <span className="text-2xl">{currency.symbol}</span>
                  <div className="flex-1">
                    <p className="font-medium">{currency.code}</p>
                    <p className="text-xs text-muted-foreground">{currency.label}</p>
                    {!isBase && currency.rate && (
                      <p className="text-xs text-muted-foreground mt-1">
                        1 {currency.code} = {safeFixed(currency.rate, 2)} {baseCurrency}
                      </p>
                    )}
                  </div>
                  {isSelected && (
                    <div className="absolute top-2 right-2">
                      <Badge className="bg-primary text-white">
                        <Check className="w-3 h-3 mr-1" />
                        Active
                      </Badge>
                    </div>
                  )}
                  {isBase && (
                    <Badge variant="outline" className="absolute bottom-2 right-2 text-xs bg-green-50 text-green-700 border-green-200">
                      Reference
                    </Badge>
                  )}
                </button>
              );
            })}
          </div>
        )}
        
        <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
          <p>
            <strong>Note:</strong> La devise principale est utilisee par defaut pour 
            les factures, rapports et affichages de prix. Les taux de change sont geres 
            par la plateforme.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
